// One sourcing event (Lane 1322; redesigned in Lane 1344, UI redesign slice 1):
// full width with the app menu folded, six tabs, and Wingman's chat docked on the
// right. Screens that were tabs before stay one click away (plan 5.9). Figures
// come only from the server; every change reloads the saved event.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageShell } from '../../components/ui/DataPrimitives';
import WorkspaceSidebarContext from '../../components/WorkspaceSidebarContext';
import ScopeTab from './ScopeTab';
import SuppliersTab from './SuppliersTab';
import NotesTermsTab from './NotesTermsTab';
import HistoryTab from './HistoryTab';
import InvitationsTab from './InvitationsTab';
import OffersTab from './OffersTab';
import RoundTab from './RoundTab';
import PublishReview from './PublishReview';
import AmendmentPanel from './AmendmentPanel';
import DeleteEventConfirm, { canDelete } from './DeleteEventConfirm';
import DocumentsTab from './DocumentsTab';
import RfpDocuments from './RfpDocuments';
import EvaluationRulesTab from './EvaluationRulesTab';
import ScenarioCompareTab from './ScenarioCompareTab';
import SourcingChatPanel from './SourcingChatPanel';
import useDockedChat from '../../components/workspace-chat/useDockedChat';
import { ChatDock } from '../../components/workspace-chat/ChatDock';
import ChatRail from '../../components/workspace-chat/ChatRail';
import useHideFeedback from '../../components/workspace-chat/useHideFeedback';
import OverviewTab from './OverviewTab';
import NegotiationTab from './NegotiationTab';
import AwardReview from './AwardReview';
import AwardTab from './AwardTab';
import { SupplierHistoryProvider } from './SupplierHistory';
// Lane 1440: RFP invitations, the tracker and Launch (only while the server sends
// summary.invitations_on, SOURCING_INVITATIONS_ENABLED).
import InvitationLetterTab from './InvitationLetter';
import LaunchDialog from './LaunchDialog';
import useSaveQueue from './useSaveQueue';
import { PartsView, SuppliersView, OffersView } from './WorkspaceLists';
import { categoryLabeler } from './scenarioText';
import { formatDueAt } from '../sourcing-portal/portalView';
import { useSourcingApi, STALE_MESSAGE } from './sourcingApi';
import '../ListLinear.css';
import './SourcingEvents.css';

// Documents (Lane 1387, Ryan): the event's documents, once the lower half of "Terms and
// documents" in the header.
const TABS = [['overview', 'Overview'], ['parts', 'Parts'], ['suppliers', 'Suppliers'],
  ['offers', 'Bids'], ['rules', 'Evaluation rules'], ['documents', 'Documents'], ['scenarios', 'Scenarios'],
  ['negotiation', 'Negotiation'], ['award', 'Award']];
// Offers and scenarios exist only after publish.
const LIVE_ONLY_TABS = ['offers', 'scenarios'];
// Negotiation: open and closed events, and read only once awarded (Lane 1354, 1395).
const OPEN_ONLY_TABS = ['negotiation'];
// The award (Lane 1395): awarded events only.
const AWARDED_ONLY_TABS = ['award'];
// Screens one click away from a tab or the header, each with "Back".
const VIEWS = {
  'edit-lines': ['parts', 'Edit lines'], qualifications: ['suppliers', 'Qualifications'],
  invitations: ['suppliers', 'Invitations and contacts'], 'bid-tools': ['offers', 'Supplier responses and notes'],
  // Terms opens from the Overview's Terms card and from "Change the event" on any tab, so
  // its Back returns to the tab it came from (Codex Lane 1387 r1).
  round: [null, 'Round and feedback'], terms: [null, 'Terms and notes'], history: ['overview', 'History'],
  'award-review': ['scenarios', 'Review the award'],
};
// The docked chat (open state, width, drag) is the shared workspace chat dock
// (Lane 1355); "srce-chat" keeps the keys this page always saved them under.
const CHAT_STORAGE_PREFIX = 'srce-chat';
const CHAT_DOCK_ID = 'srce-chat-dock';
// Lane 1387 (Ryan 2026-10-05): the round Wingman bubble opens the chat again, from its own
// strip on the right (ChatRail), so it never covers a card; no "Chat with Wingman" button
// in the header, and no Feedback button on this page for now (useHideFeedback).

function roundPill(summary) {
  if (summary.status === 'draft') return 'Draft';
  if (summary.status === 'cancelled') return 'Cancelled';
  if (summary.status === 'awarded') return 'Awarded';
  const r = summary.round;
  // "Round 1 of 2" when the buyer planned the rounds (UI redesign plan 11.1).
  const of = summary.planned_rounds && summary.planned_rounds >= summary.current_round ? ` of ${summary.planned_rounds}` : '';
  if (summary.status === 'closed' || (r && r.closed_at)) return `Round ${summary.current_round}${of} closed`;
  return `Round ${summary.current_round}${of} open${r && r.due_at ? `, due ${formatDueAt(r.due_at)}` : ''}`;
}

export default function SourcingEventWorkspace() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const api = useSourcingApi();
  const enterWorkspaceSidebar = useContext(WorkspaceSidebarContext);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [view, setView] = useState(null);                  // a screen from VIEWS, or null
  // What the chat just made or changed, for the tab to show (UI redesign 4.9).
  const [focus, setFocus] = useState(null);
  // A start for the chat's message box ("Add a rule with Wingman", "New scenario").
  const [ask, setAsk] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  // After publish, the cancel confirmation shows the exact notice and who
  // gets it (from the round review), before anything is sent.
  const [cancelNotice, setCancelNotice] = useState(null);
  // Lane 1433: the delete confirmation (drafts and cancelled events only).
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dock = useDockedChat({ storagePrefix: CHAT_STORAGE_PREFIX });
  useHideFeedback();
  const [publishing, setPublishing] = useState(false);
  // Lane 1440: the Launch dialog, and the one queue of field saves it waits for.
  const [launching, setLaunching] = useState(false);
  const saveQueue = useSaveQueue();
  // Chat records (a saved comparison, a feedback draft) do not move the
  // revision, so a chat turn also bumps this to reload the tables.
  const [chatTick, setChatTick] = useState(0);
  // Round control lives only on the Overview's Schedule card (Lane 1362, Ryan): its menu
  // opens Rounds and feedback at the chosen step.
  const [roundStart, setRoundStart] = useState(null);
  // The scenario result being reviewed for the award (Lane 1395).
  const [awardResult, setAwardResult] = useState(null);

  // The review takes the whole width: the app menu folds while this page is open.
  useEffect(() => enterWorkspaceSidebar(), [enterWorkspaceSidebar]);
  const reload = useCallback(() => api.event(publicId)
    .then((s) => { setSummary(s); setError(''); return s; })
    .catch((e) => { setError(e.status === 404 ? 'not_found' : e.message); return null; }), [api, publicId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { api.filterOptions().then(setOptions).catch(() => setOptions({ categories: [], sites: [] })); }, [api]);

  const act = async (method, path, input) => {
    try {
      await api.act(publicId, method, path, input, summary.revision);
      await reload();
      return true;
    } catch (e) {
      // A refused stale edit reloads the event AND says so; it never
      // disappears silently (Lane 1331, plan 5.2a).
      if (e.code === 'stale_revision') await reload();
      setError(e.code === 'stale_revision' ? STALE_MESSAGE : e.message);
      return false;
    }
  };

  const go = (nextTab) => { setTab(nextTab); setView(null); };
  // Lane 1440: every way into Launch (header, tracker) refuses while the header title is
  // being typed, so an unsaved title can never be launched (Codex code r1 #3).
  const openLaunch = () => {
    if (renaming) { setError('Save or cancel the new title first, then launch.'); return; }
    setLaunching(true);
  };
  // Rounds and feedback; `mode` ('close', 'extend') opens that step's review at once.
  const openRound = (mode = null) => { setRoundStart(mode ? { mode, at: Date.now() } : null); setView('round'); };
  // A start for the buyer's message, and Wingman's opening question for it (slice 2b).
  const askWingman = (text, question) => { dock.setOpen(true); setAsk({ text, question, at: Date.now() }); };
  const showFocus = (f) => {
    if (!f || !f.tab) return;
    setFocus({ ...f, at: Date.now() });
    go(f.tab);
  };

  if (error === 'not_found') {
    return (
      <PageShell className="ll"><div className="ll-wrap">
        <div className="ll-empty"><div className="ll-empty-t">Event not found</div>
          <button type="button" className="ll-clr" onClick={() => navigate('/sourcing')}>Back to sourcing events</button></div>
      </div></PageShell>
    );
  }
  if (!summary) {
    return <PageShell className="ll"><div className="ll-wrap">{error ? <div className="srce-error">{error}</div> : <div className="ll-loading">Loading event…</div>}</div></PageShell>;
  }

  const isDraft = summary.status === 'draft';
  const invOn = summary.invitations_on === true;
  const live = ['open', 'closed'].includes(summary.status);
  // Awarded and cancelled events are read-only, but their terms, documents and rounds
  // stay open for review (as the old tabs were).
  const final = ['awarded', 'cancelled'].includes(summary.status);
  const awarded = summary.status === 'awarded';
  const tabs = TABS.filter(([key]) => (!LIVE_ONLY_TABS.includes(key) || !isDraft)
    && (!OPEN_ONLY_TABS.includes(key) || live || awarded)
    && (!AWARDED_ONLY_TABS.includes(key) || awarded));
  const activeTab = tabs.some(([key]) => key === tab) ? tab : 'overview';
  const refreshKey = `${summary.revision}-${chatTick}`;
  const viewDef = view ? VIEWS[view] : null;

  const body = () => {
    if (view === 'edit-lines') return <ScopeTab api={api} eventId={publicId} summary={summary} onChanged={reload} options={options} />;
    if (view === 'qualifications') return <SuppliersTab api={api} eventId={publicId} summary={summary} onChanged={reload} />;
    if (view === 'invitations') return <InvitationsTab api={api} eventId={publicId} summary={summary} onChanged={reload} />;
    if (view === 'bid-tools') return <OffersTab api={api} eventId={publicId} summary={summary} refreshKey={chatTick} labelOf={categoryLabeler(options)} />;
    if (view === 'round') return <RoundTab refreshKey={chatTick} api={api} eventId={publicId} summary={summary} onChanged={reload} startMode={roundStart} />;
    if (view === 'history') return <HistoryTab api={api} eventId={publicId} revision={summary.revision} />;
    if (view === 'award-review' && awardResult) {
      return <AwardReview key={awardResult} api={api} eventId={publicId} resultId={awardResult}
        onCancel={() => setView(null)}
        onAwarded={async () => { setAwardResult(null); await reload(); go('award'); }} />;
    }
    if (view === 'terms') {
      return (
        <>
          {live && !summary.amendment && <AmendmentPanel api={api} eventId={publicId} summary={summary} onChanged={reload} />}
          <NotesTermsTab api={api} eventId={publicId} summary={summary} onChanged={reload}
            onOpenRound={isDraft ? undefined : () => openRound()} />
        </>
      );
    }
    switch (activeTab) {
      case 'parts': return <PartsView api={api} eventId={publicId} summary={summary} refreshKey={refreshKey} onChanged={reload} onEditLines={() => setView('edit-lines')} />;
      case 'suppliers': return invOn && isDraft
        ? <InvitationLetterTab api={api} eventId={publicId} refreshKey={refreshKey} saveQueue={saveQueue} onChanged={reload}
          onOpenQualifications={() => setView('qualifications')} />
        : <SuppliersView api={api} eventId={publicId} summary={summary} refreshKey={refreshKey} onChanged={reload}
          onOpenInvitations={() => setView('invitations')} onOpenQualifications={() => setView('qualifications')} />;
      // Lane 1440: the redesigned Documents tab while the RFP invitations switch is on.
      case 'documents': return invOn
        ? <RfpDocuments api={api} eventId={publicId} summary={summary} onChanged={reload} />
        : <DocumentsTab api={api} eventId={publicId} summary={summary} onChanged={reload} />;
      // Keyed by the event: an edit session never carries to another event (Codex code r1 #1).
      case 'offers': return <OffersView key={publicId} api={api} eventId={publicId} refreshKey={refreshKey} onOpenBidTools={() => setView('bid-tools')}
        canEdit={['open', 'closed'].includes(summary.status)} />;
      case 'rules': return <EvaluationRulesTab api={api} eventId={publicId} summary={summary} options={options}
        refreshKey={refreshKey} onAsk={askWingman} focusRuleId={focus && focus.tab === 'rules' ? focus.rule_id : null} />;
      case 'scenarios': return <ScenarioCompareTab api={api} eventId={publicId} summary={summary} options={options}
        onOpenRules={() => go('rules')} refreshKey={refreshKey} onAsk={askWingman}
        focus={focus && focus.tab === 'scenarios' ? focus : null}
        onAward={live ? (rid) => { setAwardResult(rid); setView('award-review'); } : undefined} />;
      case 'award': return <AwardTab api={api} eventId={publicId} refreshKey={refreshKey} revision={summary.revision}
        onReopened={async () => { await reload(); go('scenarios'); }} />;
      case 'negotiation': return <NegotiationTab key={publicId} api={api} eventId={publicId} refreshKey={refreshKey} onOpenOverview={() => go('overview')} onOpenRound={() => openRound()} />;
      default: return <OverviewTab api={api} eventId={publicId} summary={summary} refreshKey={refreshKey} onChanged={reload}
        onOpenParts={() => go('parts')} onOpenSuppliers={() => go('suppliers')}
        onOpenScenarios={() => go('scenarios')} onOpenHistory={() => setView('history')}
        onOpenRound={openRound} onOpenTerms={() => setView('terms')}
        saveQueue={saveQueue} onGo={invOn ? (where) => {
          if (where === 'launch') openLaunch();
          else if (where === 'round') openRound();
          else go(where);
        } : undefined} />;
    }
  };

  return (
    <PageShell className="ll srce-ws">
      <div className="srce-ws-main">
        <header className="srce-ws-head">
          <nav className="srce-crumb"><button type="button" className="ll-clr" onClick={() => navigate('/sourcing')}>Sourcing events</button> /</nav>
          <div className="srce-ws-title">
            {renaming ? (
              <span className="srce-row">
                <input className="srce-input srce-input--wide" value={name} maxLength={255} autoFocus aria-label="Event name"
                  onChange={(e) => setName(e.target.value)} />
                <button type="button" className="srce-btn srce-btn--primary" disabled={!name.trim()}
                  onClick={async () => { if (await act('PATCH', '', { name: name.trim() })) setRenaming(false); }}>Save</button>
                <button type="button" className="ll-clr" onClick={() => setRenaming(false)}>Cancel</button>
              </span>
            ) : (
              <h1 className="ll-h1 srce-ell" title={summary.name}>{summary.name}</h1>
            )}
            {!renaming && !final && (
              <button type="button" className="ll-clr" onClick={() => { setName(summary.name); setRenaming(true); }}>Rename</button>
            )}
            {/* Status only: no round control sits in this header, so none shows on every
                tab; round control is on the Overview's Schedule card (Lane 1362, Ryan). */}
            <span className={`srce-pill ${live ? 'srce-pill--info' : 'srce-pill--gray'}`}>{roundPill(summary)}</span>
            <span className="srce-push srce-row">
              {/* An awarded or cancelled event's rounds stay open to read (as before Lane 1362). */}
              {final && summary.current_round ? <button type="button" className="srce-btn" onClick={() => openRound()}>Rounds and feedback</button> : null}
              {live && <button type="button" className="srce-btn" onClick={() => setView('terms')}>Change the event</button>}
              {isDraft && !invOn && !publishing && (
                <button type="button" className="srce-btn srce-btn--primary" onClick={() => setPublishing(true)}>Review and publish</button>
              )}
              {/* Lane 1440: a short Launch dialog; hidden while the title is being typed in
                  the header, so an unsaved title can never be launched (Codex plan r2). */}
              {isDraft && invOn && !renaming && (
                <button type="button" className="srce-btn srce-btn--primary" onClick={openLaunch}>Launch</button>
              )}
              {['draft', 'open', 'closed'].includes(summary.status) && !confirmCancel && (
                <button type="button" className="srce-btn srce-btn--quiet" onClick={() => {
                  setConfirmCancel(true);
                  setConfirmDelete(false);
                  setCancelNotice(null);
                  if (!isDraft) {
                    api.roundReview(publicId)
                      .then((r) => setCancelNotice(r.cancel_notice || { suppliers: [] }))
                      .catch((e) => setCancelNotice({ error: e.message }));
                  }
                }}>Cancel event</button>
              )}
              {canDelete(summary) && !confirmDelete && (
                <button type="button" className="srce-btn srce-btn--neg"
                  onClick={() => { setConfirmCancel(false); setConfirmDelete(true); }}>Delete event</button>
              )}
            </span>
          </div>
          {confirmDelete && canDelete(summary) && (
            <DeleteEventConfirm api={api} event={summary} onCancel={() => setConfirmDelete(false)}
              onDeleted={(e) => navigate('/sourcing', { state: { deletedEventName: e.name } })} />
          )}
          {confirmCancel && (
            <div className="srce-panel srce-col">
              <span className="srce-crit">Cancel this event? It becomes read-only.</span>
              {!isDraft && !cancelNotice && <span className="ll-loading">Loading what suppliers will be told…</span>}
              {!isDraft && cancelNotice && cancelNotice.error && (
                <span className="srce-error" role="alert">{cancelNotice.error}</span>
              )}
              {!isDraft && cancelNotice && !cancelNotice.error && (
                cancelNotice.suppliers.length === 0 ? (
                  <span>No supplier has joined yet, so no one is told.</span>
                ) : (
                  <span className="srce-notice">
                    <span className="srce-strong">{cancelNotice.suppliers.join(', ')}</span>
                    {cancelNotice.suppliers.length === 1 ? ' gets' : ' get'} this notice:
                    <span className="srce-block"><span className="srce-strong">{cancelNotice.title}</span></span>
                    <span className="srce-block">{cancelNotice.body}</span>
                  </span>
                )
              )}
              <span className="srce-row">
                <button type="button" className="srce-btn srce-btn--primary"
                  disabled={!isDraft && (!cancelNotice || Boolean(cancelNotice.error))}
                  onClick={async () => { setConfirmCancel(false); await act('POST', '/cancel', {}); }}>Yes, cancel</button>
                <button type="button" className="ll-clr" onClick={() => setConfirmCancel(false)}>Keep it</button>
              </span>
            </div>
          )}
          <div className="srce-ws-tabs" role="tablist">
            {tabs.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={!view && activeTab === key}
                className={`srce-ws-tab${!view && activeTab === key ? ' srce-ws-tab--on' : ''}`} onClick={() => go(key)}>{label}</button>
            ))}
          </div>
        </header>

        <div className="srce-ws-body">
          {error && <div className="srce-error" role="alert">{error}</div>}
          {launching && (
            <LaunchDialog api={api} eventId={publicId} saveQueue={saveQueue} onClose={() => setLaunching(false)}
              onLaunched={reload} onGoInvitations={() => { setLaunching(false); setView('invitations'); }} />
          )}
          {publishing && (
            <PublishReview api={api} eventId={publicId} onClose={() => setPublishing(false)} onPublished={reload} onSaved={reload}
              onDone={(nextTab) => { setPublishing(false); if (nextTab === 'invitations') setView('invitations'); else go('overview'); }} />
          )}
          {live && summary.amendment && <AmendmentPanel api={api} eventId={publicId} summary={summary} onChanged={reload} />}
          {viewDef && (
            <div className="srce-row">
              <button type="button" className="ll-clr" onClick={() => setView(null)}>
                Back{viewDef[0] ? ` to ${TABS.find(([k]) => k === viewDef[0])[1]}` : ''}</button>
              <span className="srce-strong">{viewDef[1]}</span>
            </div>
          )}
          {/* Supplier history marks on Offers, Scenarios and the result (Lane 1395 part 2):
              read once per event after the page shows; a rule added from the pop-up reloads
              the tabs. */}
          <SupplierHistoryProvider api={api} eventId={publicId} live={live || summary.status === 'awarded'}
            refreshKey={refreshKey} currency={(summary.terms && summary.terms.currency) || 'USD'}
            canAddRules={live} onRulesChanged={() => { setChatTick((n) => n + 1); reload(); }}>
            {body()}
          </SupplierHistoryProvider>
        </div>
      </div>
      <ChatRail open={dock.open} onOpen={() => dock.setOpen(true)} controls={CHAT_DOCK_ID} />
      <ChatDock dock={dock} id={CHAT_DOCK_ID}>
        <SourcingChatPanel api={api} eventId={publicId} revision={summary.revision}
          currency={(summary.terms && summary.terms.currency) || 'USD'}
          refreshKey={refreshKey}
          onChanged={() => { setChatTick((n) => n + 1); return reload(); }} onClose={() => dock.setOpen(false)}
          onOpenOffers={isDraft ? undefined : () => go('offers')}
          onOpenRound={isDraft ? undefined : () => openRound()}
          onOpenRules={() => go('rules')} ask={ask} onFocus={showFocus}
          onOpenScenario={isDraft ? undefined : (id) => showFocus({ tab: 'scenarios', scenario_id: id })} />
      </ChatDock>
    </PageShell>
  );
}
